// The short looping clips a guide can show (":::clip <id>"): real components with demo data, recorded frame by frame
// by scripts/guide-shots/shoot.mjs (CLIPS) and encoded there as H.264 MP4 (no sound, ≤ 1280 px wide), with the last
// frame as a WebP poster. The poster is what people who prefer reduced motion see, and what shows until the clip is
// on screen; the caption says in words what the clip shows, so nothing is only in the animation.

import type { GuideImage } from "./images";

const VIDEOS = import.meta.glob("/src/assets/guides/*.mp4", { eager: true, import: "default" }) as Record<string, string>;
const FILES = import.meta.glob("/src/assets/guides/*-poster-*.webp", { eager: true, import: "default" }) as Record<string, string>;

export interface GuideClip {
  /** The MP4 file. */
  src: string;
  /** The poster (the clip's last frame) at several widths. */
  poster: GuideImage;
  /** Length, for the "plays for …" hint. */
  seconds: number;
}

const clip = (id: string, width: number, height: number, seconds: number): GuideClip => {
  const src = Object.entries(VIDEOS).find(([p]) => p.endsWith(`/${id}.mp4`))?.[1];
  const posters = Object.entries(FILES)
    .flatMap(([p, url]) => {
      const m = new RegExp(`/${id}-poster-(\\d+)\\.webp$`).exec(p);
      return m ? [[url, Number(m[1])] as [string, number]] : [];
    })
    .sort((a, b) => a[1] - b[1]);
  if (!src || !posters.length) throw new Error(`no files for guide clip ${id}`);
  return {
    src,
    seconds,
    poster: {
      src: posters[posters.length - 1][0],
      srcSet: posters.map(([u, w]) => `${u} ${w}w`).join(", "),
      width,
      height,
    },
  };
};

export const GUIDE_CLIPS: Record<string, GuideClip> = {
  // Revenue split guide (src/content/guides/split-gallery-revenue).
  "split-add-party-clip": clip("split-add-party-clip", 1280, 1000, 19),
  "split-example-clip": clip("split-example-clip", 1280, 884, 14),
  "split-credit-clip": clip("split-credit-clip", 1280, 1152, 10),
};
